import { useState, useEffect, useCallback } from 'react'
import {
  X,
  Plus,
  Trash2,
  Save,
  Shield,
  Users,
  ChevronDown,
  ChevronUp,
  Check,
  Lock,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adminApi, RBACRole, RBACPermission, AdminUserRoleItem } from '../../lib/api/admin'
import { usePermissionStore } from '../../stores/permissionStore'

// ── Constants ──────────────────────────────────────────────────────────────────

const MODULES: { key: string; label: string; allowedActions: (keyof RBACPermission)[] }[] = [
  { key: 'dashboard',    label: 'Dashboard',    allowedActions: ['can_view'] },
  { key: 'users',        label: 'Users',        allowedActions: ['can_view', 'can_create', 'can_edit', 'can_delete'] },
  { key: 'sessions',     label: 'Sessions',     allowedActions: ['can_view'] },
  { key: 'applications', label: 'Applications', allowedActions: ['can_view', 'can_edit'] },
  { key: 'reports',      label: 'Reports',      allowedActions: ['can_view'] },
  { key: 'settings',     label: 'Settings',     allowedActions: ['can_view', 'can_edit'] },
  { key: 'security',     label: 'Security',     allowedActions: ['can_view', 'can_create', 'can_edit', 'can_delete'] },
  { key: 'content',      label: 'Content',      allowedActions: ['can_view', 'can_create'] },
]

const ACTIONS: { key: keyof RBACPermission; label: string }[] = [
  { key: 'can_view', label: 'View' },
  { key: 'can_create', label: 'Create' },
  { key: 'can_edit', label: 'Edit' },
  { key: 'can_delete', label: 'Delete' },
]

const emptyPermissions = (): RBACPermission[] =>
  MODULES.map((m) => ({
    module: m.key,
    can_view: false,
    can_create: false,
    can_edit: false,
    can_delete: false,
  }))

// ── Types ──────────────────────────────────────────────────────────────────────

interface Props {
  onClose: () => void
}

type Tab = 'roles' | 'assignments'

// ── Component ─────────────────────────────────────────────────────────────────

export default function AdminAccessControl({ onClose }: Props) {
  const { can, is_super_admin } = usePermissionStore()
  const canCreate = is_super_admin || can('security', 'create')
  const canEdit   = is_super_admin || can('security', 'edit')
  const canDelete = is_super_admin || can('security', 'delete')

  const [tab, setTab] = useState<Tab>('roles')
  const [roles, setRoles] = useState<RBACRole[]>([])
  const [adminUsers, setAdminUsers] = useState<AdminUserRoleItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Role editor state
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingDesc, setEditingDesc] = useState('')
  const [editingPerms, setEditingPerms] = useState<RBACPermission[]>(emptyPermissions())
  const [isCreating, setIsCreating] = useState(false)

  // Permission validation error (view dependency)
  const [viewError, setViewError] = useState<string>('')

  // Assignment state
  const [assigningUserId, setAssigningUserId] = useState<number | null>(null)
  const [assignRoleId, setAssignRoleId] = useState<string>('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [r, u] = await Promise.all([
        adminApi.getRoles(),
        adminApi.getAdminUsersWithRoles(),
      ])
      setRoles(r.filter((role) => !role.is_system))
      setAdminUsers(u)
    } catch {
      setError('Failed to load access control data.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // ── Role selection ───────────────────────────────────────────────────────────

  const selectRole = (role: RBACRole) => {
    setIsCreating(false)
    setSelectedRoleId(role.id)
    setEditingName(role.name)
    setEditingDesc(role.description ?? '')
    const merged = emptyPermissions().map((ep) => {
      const found = role.permissions.find((p) => p.module === ep.module)
      return found ?? ep
    })
    setEditingPerms(merged)
  }

  const startCreate = () => {
    setIsCreating(true)
    setSelectedRoleId(null)
    setEditingName('')
    setEditingDesc('')
    setEditingPerms(emptyPermissions())
  }

  const cancelEdit = () => {
    setIsCreating(false)
    setSelectedRoleId(null)
  }

  // ── Permission toggle helpers ────────────────────────────────────────────────

  const VIEW_ERROR_MSG = 'View permission is mandatory when Create, Edit, or Delete is selected.'

  const togglePerm = (module: string, action: keyof RBACPermission) => {
    setViewError('')
    setEditingPerms((prev) => {
      const row = prev.find((p) => p.module === module)
      if (!row) return prev
      const newVal = !row[action]

      // Block unchecking View while any write permission is active
      if (action === 'can_view' && !newVal) {
        if (row.can_create || row.can_edit || row.can_delete) {
          setViewError(VIEW_ERROR_MSG)
          return prev
        }
      }

      return prev.map((p) => {
        if (p.module !== module) return p
        const updated = { ...p, [action]: newVal }
        // Auto-enable View whenever a write action is turned on
        if (newVal && action !== 'can_view' && !updated.can_view) {
          updated.can_view = true
        }
        return updated
      })
    })
  }

  const toggleRow = (module: string) => {
    const mod = MODULES.find((m) => m.key === module)
    const row = editingPerms.find((p) => p.module === module)
    if (!row || !mod) return
    const allOn = mod.allowedActions.every((a) => row[a])
    setEditingPerms((prev) =>
      prev.map((p) => {
        if (p.module !== module) return p
        const patch = mod.allowedActions.reduce(
          (acc, a) => ({ ...acc, [a]: !allOn }),
          {} as Partial<RBACPermission>
        )
        return { ...p, ...patch }
      })
    )
  }

  const toggleCol = (action: keyof RBACPermission) => {
    setViewError('')
    const modulesWithAction = MODULES.filter((m) => m.allowedActions.includes(action)).map((m) => m.key)
    const allOn = editingPerms.filter((p) => modulesWithAction.includes(p.module)).every((p) => p[action])

    // Block turning off the entire View column if any module still has write perms
    if (action === 'can_view' && allOn) {
      const anyWriteActive = editingPerms.some(
        (p) => modulesWithAction.includes(p.module) && (p.can_create || p.can_edit || p.can_delete)
      )
      if (anyWriteActive) {
        setViewError(VIEW_ERROR_MSG)
        return
      }
    }

    setEditingPerms((prev) =>
      prev.map((p) => {
        if (!modulesWithAction.includes(p.module)) return p
        const updated = { ...p, [action]: !allOn }
        // When enabling a non-View column, auto-enable View for that module too
        if (!allOn && action !== 'can_view' && !updated.can_view) {
          updated.can_view = true
        }
        return updated
      })
    )
  }

  // ── Save role ────────────────────────────────────────────────────────────────

  const saveRole = async () => {
    if (!editingName.trim()) {
      toast.error('Role name is required.')
      return
    }
    const hasAnyPermission = editingPerms.some(
      (p) => p.can_view || p.can_create || p.can_edit || p.can_delete
    )
    if (!hasAnyPermission) {
      toast.error('Select at least one permission before saving the role.')
      return
    }
    setSaving(true)
    setError('')
    try {
      if (isCreating) {
        const created = await adminApi.createRole({
          name: editingName.trim(),
          description: editingDesc.trim() || undefined,
          permissions: editingPerms,
        })
        setRoles((prev) => [...prev, created])
        selectRole(created)
        setIsCreating(false)
        toast.success(`Role "${created.name}" created successfully.`)
      } else if (selectedRoleId !== null) {
        const updated = await adminApi.updateRole(selectedRoleId, {
          name: editingName.trim(),
          description: editingDesc.trim() || undefined,
          permissions: editingPerms,
        })
        setRoles((prev) => prev.map((r) => (r.id === selectedRoleId ? updated : r)))
        setSelectedRoleId(updated.id)
        toast.success(`Changes to "${updated.name}" saved.`)
      }
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { detail?: string; message?: string } } })?.response?.data
      const msg = data?.detail ?? data?.message
      setError(msg ?? 'Failed to save role.')
      toast.error(msg ?? 'Failed to save role.')
    } finally {
      setSaving(false)
    }
  }

  // ── Delete role ──────────────────────────────────────────────────────────────

  const deleteRole = async (roleId: number) => {
    if (!confirm('Delete this role? Users assigned to it will lose their role assignment.')) return
    setSaving(true)
    setError('')
    try {
      const roleToDelete = roles.find((r) => r.id === roleId)
      await adminApi.deleteRole(roleId)
      setRoles((prev) => prev.filter((r) => r.id !== roleId))
      if (selectedRoleId === roleId) cancelEdit()
      toast.success(`Role "${roleToDelete?.name ?? 'Role'}" deleted.`)
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg ?? 'Failed to delete role.')
      toast.error(msg ?? 'Failed to delete role.')
    } finally {
      setSaving(false)
    }
  }

  // ── Assign role ──────────────────────────────────────────────────────────────

  const saveAssignment = async (userId: number) => {
    if (assignRoleId === '') return
    setSaving(true)
    setError('')
    try {
      const updated = await adminApi.assignUserRole(userId, Number(assignRoleId))
      setAdminUsers((prev) =>
        prev.map((u) =>
          u.user_id === userId
            ? { ...u, role_id: updated.role_id, role_name: updated.role_name }
            : u
        )
      )
      setAssigningUserId(null)
      setAssignRoleId('')
      toast.success(`Role "${updated.role_name}" assigned successfully.`)
    } catch {
      setError('Failed to assign role.')
      toast.error('Failed to assign role.')
    } finally {
      setSaving(false)
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          background: 'white', borderRadius: 16,
          width: '100%', maxWidth: 960,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(30,36,50,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={18} style={{ color: 'var(--primary-navy)' }} />
            </div>
            <div>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Access Control</h2>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Manage roles and permissions for admin accounts</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', padding: '0 24px' }}>
          {([['roles', 'Roles & Permissions', <Shield size={14} />], ['assignments', 'User Assignments', <Users size={14} />]] as const).map(([key, label, icon]) => (
            <button
              key={key}
              onClick={() => setTab(key as Tab)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '12px 0', marginRight: 24,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: tab === key ? 600 : 400,
                color: tab === key ? 'var(--primary-navy)' : 'var(--text-muted)',
                borderBottom: tab === key ? '2px solid var(--primary-navy)' : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >{icon}{label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : tab === 'roles' ? (
            <RolesTab
              roles={roles}
              selectedRoleId={selectedRoleId}
              isCreating={isCreating}
              editingName={editingName}
              editingDesc={editingDesc}
              editingPerms={editingPerms}
              saving={saving}
              error={error}
              viewError={viewError}
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onSelectRole={selectRole}
              onStartCreate={startCreate}
              onCancelEdit={cancelEdit}
              onDeleteRole={deleteRole}
              onSaveRole={saveRole}
              onChangeName={setEditingName}
              onChangeDesc={setEditingDesc}
              onTogglePerm={togglePerm}
              onToggleRow={toggleRow}
              onToggleCol={toggleCol}
              onClearViewError={() => setViewError('')}
            />
          ) : (
            <AssignmentsTab
              adminUsers={adminUsers}
              roles={roles}
              assigningUserId={assigningUserId}
              assignRoleId={assignRoleId}
              saving={saving}
              error={error}
              canEdit={canEdit}
              onStartAssign={(userId, currentRoleId, firstRoleId) => {
                setAssigningUserId(userId)
                setAssignRoleId(currentRoleId !== null ? String(currentRoleId) : (firstRoleId !== null ? String(firstRoleId) : ''))
              }}
              onCancelAssign={() => { setAssigningUserId(null); setAssignRoleId('') }}
              onSaveAssignment={saveAssignment}
              onChangeAssignRole={setAssignRoleId}
            />
          )}
        </div>
      </div>
      <style>{`
        /* Two-panel layout: sidebar + editor side by side on desktop */
        .aac-roles-layout { display: flex; flex: 1; overflow: hidden; }
        .aac-sidebar { width: 220px; border-right: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; flex-shrink: 0; }
        .aac-editor { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }

        /* Role name + description row */
        .aac-name-row { padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; gap: 12px; align-items: flex-start; }

        /* Permission matrix area */
        .aac-perm-area { flex: 1; overflow-y: auto; padding: 16px 20px; }
        .aac-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .aac-table-scroll table { min-width: 320px; }

        /* Mobile: stack sidebar on top of editor */
        @media (max-width: 640px) {
          .aac-roles-layout { flex-direction: column; overflow-y: auto; }
          .aac-sidebar { width: 100%; border-right: none; border-bottom: 1px solid var(--border); max-height: 180px; flex-shrink: 0; }
          .aac-editor { overflow: visible; min-height: 0; }
          .aac-name-row { flex-direction: column; gap: 8px; padding: 12px 14px; }
          .aac-name-row > div { flex: none !important; width: 100%; }
          .aac-perm-area { padding: 12px 14px; overflow-y: visible; }
          .aac-table-scroll { overflow-x: auto; }
        }
      `}</style>
    </div>
  )
}

// ── Roles Tab ──────────────────────────────────────────────────────────────────

interface RolesTabProps {
  roles: RBACRole[]
  selectedRoleId: number | null
  isCreating: boolean
  editingName: string
  editingDesc: string
  editingPerms: RBACPermission[]
  saving: boolean
  error: string
  viewError: string
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  onSelectRole: (r: RBACRole) => void
  onStartCreate: () => void
  onCancelEdit: () => void
  onDeleteRole: (id: number) => void
  onSaveRole: () => void
  onChangeName: (v: string) => void
  onChangeDesc: (v: string) => void
  onTogglePerm: (module: string, action: keyof RBACPermission) => void
  onToggleRow: (module: string) => void
  onToggleCol: (action: keyof RBACPermission) => void
  onClearViewError: () => void
}

function RolesTab({
  roles, selectedRoleId, isCreating,
  editingName, editingDesc, editingPerms,
  saving, error, viewError,
  canCreate, canEdit, canDelete,
  onSelectRole, onStartCreate, onCancelEdit, onDeleteRole, onSaveRole,
  onChangeName, onChangeDesc, onTogglePerm, onToggleRow, onToggleCol,
  onClearViewError,
}: RolesTabProps) {
  const isEditing = isCreating || selectedRoleId !== null
  const selectedRole = roles.find((r) => r.id === selectedRoleId) ?? null
  // Permissions are editable for an existing role only with canEdit; for a new role only with canCreate
  const permsEditable = isCreating ? canCreate : canEdit

  return (
    <div className="aac-roles-layout">
      {/* Role list sidebar */}
      <div className="aac-sidebar">
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          {canCreate ? (
            <button
              className="btn btn-primary"
              style={{ width: '100%', fontSize: 13, padding: '7px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              onClick={onStartCreate}
            >
              <Plus size={14} /> New Role
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)', padding: '7px 0' }}>
              <Lock size={12} /> View only
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {roles.map((role) => (
            <div
              key={role.id}
              onClick={() => onSelectRole(role)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '9px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 2,
                background: selectedRoleId === role.id ? 'var(--primary-navy)' : 'transparent',
                color: selectedRoleId === role.id ? 'white' : 'var(--text-primary)',
                transition: 'background 0.1s',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{role.name}</div>
                <div style={{ fontSize: 10, opacity: 0.7, marginTop: 1 }}>
                  {role.hierarchy_level === 1 ? 'Sub Admin' : `Level ${role.hierarchy_level}`}
                </div>
              </div>
              {!role.is_system && canDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteRole(role.id) }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 2, marginLeft: 4,
                    color: selectedRoleId === role.id ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)',
                    flexShrink: 0,
                  }}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
          {roles.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 8px' }}>No roles yet. Create one.</p>
          )}
        </div>
      </div>

      {/* Editor panel */}
      <div className="aac-editor">
        {!isEditing ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            {canEdit || canCreate ? 'Select a role to view or edit, or create a new one.' : 'Select a role to view its permissions.'}
          </div>
        ) : (
          <>
            {/* View-only notice */}
            {!permsEditable && !selectedRole?.is_system && (
              <div style={{ margin: '12px 20px 0', padding: '8px 12px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, fontSize: 12, color: '#854d0e', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Lock size={13} style={{ flexShrink: 0 }} />
                You have <strong>view-only</strong> access. Contact an admin to make changes.
              </div>
            )}

            {/* Role name / desc */}
            <div className="aac-name-row">
              <div style={{ flex: 1 }}>
                <input
                  value={editingName}
                  onChange={(e) => onChangeName(e.target.value)}
                  placeholder="Role name (e.g. Manager)"
                  disabled={selectedRole?.is_system || !permsEditable}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8,
                    fontSize: 14, fontWeight: 600, outline: 'none',
                    background: (selectedRole?.is_system || !permsEditable) ? 'var(--accent-light)' : 'white',
                  }}
                />
              </div>
              <div style={{ flex: 2 }}>
                <input
                  value={editingDesc}
                  onChange={(e) => onChangeDesc(e.target.value)}
                  placeholder="Description (optional)"
                  disabled={selectedRole?.is_system || !permsEditable}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '7px 10px',
                    border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none',
                    background: (selectedRole?.is_system || !permsEditable) ? 'var(--accent-light)' : 'white',
                  }}
                />
              </div>
            </div>

            {/* System role notice */}
            {selectedRole?.is_system && (
              <div style={{ margin: '12px 20px 0', padding: '8px 12px', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 8, fontSize: 12, color: '#856404' }}>
                System roles have full access to all modules and cannot be modified.
              </div>
            )}

            {/* Permission matrix */}
            {!selectedRole?.is_system && (
              <div className="aac-perm-area">
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Module Permissions
                </div>
                {viewError && (
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 10, marginBottom: 12, padding: '10px 14px',
                      background: 'rgba(245,101,101,0.08)', border: '1px solid rgba(245,101,101,0.35)',
                      borderRadius: 8, fontSize: 13, color: 'var(--danger)',
                    }}
                  >
                    <span>{viewError}</span>
                    <button
                      onClick={onClearViewError}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 2, flexShrink: 0 }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                <div className="aac-table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 320 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', padding: '6px 8px', width: '40%' }}>Module</th>
                      {ACTIONS.map((a) => (
                        <th
                          key={a.key}
                          style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', padding: '6px 8px', cursor: permsEditable ? 'pointer' : 'default', userSelect: 'none' }}
                          onClick={() => permsEditable && onToggleCol(a.key)}
                        >
                          {a.label}
                        </th>
                      ))}
                      <th style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', padding: '6px 8px' }}>All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MODULES.map((mod, idx) => {
                      const perm = editingPerms.find((p) => p.module === mod.key)!
                      const allOn = mod.allowedActions.every((a) => perm[a])
                      return (
                        <tr key={mod.key} style={{ background: idx % 2 === 0 ? 'var(--accent-light)' : 'white', borderRadius: 6 }}>
                          <td style={{ padding: '8px 8px', fontSize: 13, fontWeight: 500 }}>{mod.label}</td>
                          {ACTIONS.map((a) => (
                            <td key={a.key} style={{ textAlign: 'center', padding: '8px 8px' }}>
                              {mod.allowedActions.includes(a.key) ? (
                                <Checkbox
                                  checked={!!perm[a.key]}
                                  onChange={() => onTogglePerm(mod.key, a.key)}
                                  disabled={!permsEditable}
                                />
                              ) : (
                                <span style={{ color: 'var(--border)', fontSize: 14, lineHeight: 1 }}>—</span>
                              )}
                            </td>
                          ))}
                          <td style={{ textAlign: 'center', padding: '8px 8px' }}>
                            <Checkbox
                              checked={allOn}
                              onChange={() => onToggleRow(mod.key)}
                              color="var(--primary-navy)"
                              disabled={!permsEditable}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {/* Footer */}
            {error && (
              <div style={{ margin: '0 20px', padding: '8px 12px', background: 'rgba(245,101,101,0.1)', border: '1px solid rgba(245,101,101,0.3)', borderRadius: 8, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
            )}
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" style={{ fontSize: 13, padding: '7px 16px' }} onClick={onCancelEdit}>
                {permsEditable ? 'Cancel' : 'Close'}
              </button>
              {!selectedRole?.is_system && permsEditable && (
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 13, padding: '7px 16px', display: 'flex', alignItems: 'center', gap: 6 }}
                  onClick={onSaveRole}
                  disabled={saving || !editingName.trim() || !editingPerms.some((p) => p.can_view || p.can_create || p.can_edit || p.can_delete)}
                >
                  <Save size={14} />{saving ? 'Saving...' : isCreating ? 'Create Role' : 'Save Changes'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Assignments Tab ────────────────────────────────────────────────────────────

interface AssignmentsTabProps {
  adminUsers: AdminUserRoleItem[]
  roles: RBACRole[]
  assigningUserId: number | null
  assignRoleId: string
  saving: boolean
  error: string
  canEdit: boolean
  onStartAssign: (userId: number, currentRoleId: number | null, firstRoleId: number | null) => void
  onCancelAssign: () => void
  onSaveAssignment: (userId: number) => void
  onChangeAssignRole: (v: string) => void
}

function AssignmentsTab({
  adminUsers, roles,
  assigningUserId, assignRoleId,
  saving, error,
  canEdit,
  onStartAssign, onCancelAssign, onSaveAssignment, onChangeAssignRole,
}: AssignmentsTabProps) {
  const [expanded, setExpanded] = useState<number | null>(null)

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        Assign roles to admin accounts to control their access permissions.
        {!canEdit && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8, color: '#854d0e', background: '#fef9c3', border: '1px solid #fde047', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>
            <Lock size={11} /> View only
          </span>
        )}
      </p>

      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(245,101,101,0.1)', border: '1px solid rgba(245,101,101,0.3)', borderRadius: 8, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {adminUsers.map((u) => {
          const isAssigning = assigningUserId === u.user_id
          const isExpanded = expanded === u.user_id
          const assignedRole = roles.find((r) => r.id === u.role_id)

          return (
            <div key={u.user_id} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'white', cursor: 'pointer' }}
                onClick={() => setExpanded(isExpanded ? null : u.user_id)}
              >
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                  {(u.user_email ?? 'A')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.user_email ?? `User #${u.user_id}`}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                    {u.role_name
                      ? <span style={{ color: 'var(--primary-navy)', fontWeight: 500 }}>{u.role_name}</span>
                      : <span style={{ color: 'var(--text-muted)' }}>No role assigned</span>}
                  </div>
                </div>
                {isExpanded ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
              </div>

              {isExpanded && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px', background: 'var(--accent-light)' }}>
                  {/* Current permissions summary */}
                  {assignedRole && assignedRole.permissions.length > 0 && !isAssigning && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Current Permissions</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {assignedRole.permissions
                          .filter((p) => p.can_view || p.can_create || p.can_edit || p.can_delete)
                          .map((p) => {
                            const acts = ACTIONS.filter((a) => p[a.key]).map((a) => a.label)
                            return (
                              <span key={p.module} style={{ fontSize: 11, padding: '3px 8px', background: 'white', border: '1px solid var(--border)', borderRadius: 12 }}>
                                {MODULES.find((m) => m.key === p.module)?.label}: {acts.join(', ')}
                              </span>
                            )
                          })}
                      </div>
                    </div>
                  )}

                  {isAssigning ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <select
                        value={assignRoleId}
                        onChange={(e) => onChangeAssignRole(e.target.value)}
                        style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' }}
                      >
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: 13, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 5 }}
                        onClick={() => onSaveAssignment(u.user_id)}
                        disabled={saving || assignRoleId === ''}
                      >
                        <Check size={13} />{saving ? 'Saving...' : 'Assign'}
                      </button>
                      <button className="btn btn-secondary" style={{ fontSize: 13, padding: '7px 12px' }} onClick={onCancelAssign}>Cancel</button>
                    </div>
                  ) : canEdit && !u.is_super_admin ? (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: 12, padding: '6px 14px' }}
                      onClick={() => onStartAssign(u.user_id, u.role_id, roles[0]?.id ?? null)}
                    >
                      Change Role
                    </button>
                  ) : u.is_super_admin ? null : (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Lock size={11} /> No permission to change roles
                    </span>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {adminUsers.length === 0 && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, padding: 32 }}>No admin accounts found.</p>
        )}
      </div>
    </div>
  )
}

// ── Checkbox ───────────────────────────────────────────────────────────────────

function Checkbox({ checked, onChange, color = '#48bb78', disabled = false }: { checked: boolean; onChange: () => void; color?: string; disabled?: boolean }) {
  return (
    <div
      onClick={disabled ? undefined : onChange}
      style={{
        width: 18, height: 18,
        borderRadius: 4,
        border: `2px solid ${disabled ? 'var(--border)' : checked ? color : 'var(--border)'}`,
        background: disabled ? (checked ? '#cbd5e1' : '#f1f5f9') : checked ? color : 'white',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {checked && <Check size={11} color="white" strokeWidth={3} />}
    </div>
  )
}
